/**
 * Auth: simple password gate at /login only (sessionStorage). No Better Auth, no Clerk, no tokens.
 */
import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import apiClient from '../lib/api';

/** SessionStorage key for simple Brightstone password gate (no DB). */
export const BRIGHTSTONE_STORAGE_KEY = 'brightstone_unlocked';

const BRIGHTSTONE_DUMMY_USER = {
  id: 'brightstone',
  email: 'internal@brightstone',
  first_name: 'User',
  last_name: '',
  company_name: 'Brightstone',
  role: 'user',
};

function isBrightstoneUnlocked(): boolean {
  if (typeof window === 'undefined') return false;
  return sessionStorage.getItem(BRIGHTSTONE_STORAGE_KEY) === '1';
}

interface User {
  id: string;
  email: string;
  first_name?: string;
  last_name?: string;
  company_name?: string;
  role?: string;
}

interface AuthContextType {
  user: User | null;
  loading: boolean;
  error: string | null;
  login: (email: string, password: string) => Promise<{ needs2FA: boolean; signInAttemptId?: string }>;
  completeTwoFactorLogin: (accessToken: string, user: User) => void;
  completeClerk2FA: (code: string) => Promise<void>;
  register: (data: any) => Promise<void>;
  logout: () => void;
  checkAuth: () => Promise<void>;
  clerkUser: any | null;
  clerkIsLoaded: boolean;
  clerkIsSignedIn: boolean | undefined;
  needsPasswordSetup: boolean;
  refreshPasswordStatus: () => void;
  clerkSignInAttempt: any | null;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Signed in = simple password gate unlocked (sessionStorage)
  const signedIn = isBrightstoneUnlocked();

  useEffect(() => {
    if (signedIn) {
      setUser(BRIGHTSTONE_DUMMY_USER);
    } else {
      setUser(null);
    }
  }, [signedIn]);

  const refreshPasswordStatus = () => {};
  const completeClerk2FA = async (_code: string) => {
    throw new Error('Two-factor authentication is not used');
  };
  const completeTwoFactorLogin = (_accessToken: string, userData: User) => {
    setUser(userData);
  };

  const checkAuth = async () => {
    if (isBrightstoneUnlocked()) {
      setUser(BRIGHTSTONE_DUMMY_USER);
    } else {
      setUser(null);
    }
  };

  const login = async (email: string, password: string): Promise<{ needs2FA: boolean; signInAttemptId?: string }> => {
    setLoading(true);
    setError(null);
    try {
      await apiClient.auth.login(email, password);
      // Simple gate is handled on /login page; if using API login elsewhere, sync user from API
      const userData = await apiClient.auth.me().catch(() => null);
      if (userData) setUser(userData);
      return { needs2FA: false };
    } catch (err: any) {
      const msg = err?.message || err?.response?.data?.detail || 'Login failed';
      setError(msg);
      throw new Error(msg);
    } finally {
      setLoading(false);
    }
  };

  const register = async (data: any) => {
    setLoading(true);
    setError(null);
    try {
      await apiClient.auth.register(data);
      await login(data.email, data.password);
    } catch (err: any) {
      setError(err?.message || 'Registration failed');
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const logout = () => {
    sessionStorage.removeItem(BRIGHTSTONE_STORAGE_KEY);
    setUser(null);
    setError(null);
    localStorage.removeItem('auth_token');
    window.location.href = '/login';
  };

  const value: AuthContextType = {
    user: signedIn ? BRIGHTSTONE_DUMMY_USER : user,
    loading,
    error,
    login,
    completeTwoFactorLogin,
    completeClerk2FA,
    register,
    logout,
    checkAuth,
    clerkUser: null,
    clerkIsLoaded: true,
    clerkIsSignedIn: signedIn,
    needsPasswordSetup: false,
    refreshPasswordStatus,
    clerkSignInAttempt: null,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
